/**
 * "Hvem kan ta den?" (V19 cover, owner 2026-10-08): who could take an open shift, or one whose
 * person is away, from what the page already holds. A hint, not a rule check: the server judges
 * the write, and the panel shows its rule entries after it. Three kinds, as V19 draws them:
 * clear, to look at (a rule the plan would come near), and blocked (cannot work then).
 */
import type { ApiShift } from '@/api/data';
import { fullName } from '@/ui/format';
import type { Row } from './model';
import { dateOf } from './model';

export interface Candidate {
  userId: number;
  name: string;
  /** hours planned this week before this shift, and the agreed week when this reader may know it */
  hours: number;
  norm: number | null;
  kind: 'clear' | 'review' | 'blocked';
  /** why, in words: empty for clear */
  why: string[];
}

const H = 36e5;
const REST = 11;

export function candidates(s: ApiShift, rows: Row[], shifts: ApiShift[]): Candidate[] {
  const d = dateOf(s);
  const from = Date.parse(s.start_time);
  const to = Date.parse(s.end_time);
  const len = (to - from) / H;
  const out: Candidate[] = [];
  for (const r of rows) {
    if (r.user.id === s.user_id) continue;
    const why: string[] = [];
    let blocked = false;
    const cell = r.cells.get(d);
    if (cell?.absence && cell.absence.status !== 'declined') {
      why.push('fravær');
      blocked = true;
    }
    if (cell?.avail?.kind === 'unavail') {
      why.push('ikke tilgjengelig');
      blocked = true;
    } else if (cell?.avail?.kind === 'partly') why.push(`kun ${cell.avail.from}–${cell.avail.to}`);
    const mine = shifts.filter((x) => x.user_id === r.user.id && x.id !== s.id);
    if (mine.some((x) => Date.parse(x.start_time) < to && Date.parse(x.end_time) > from)) {
      why.push('har vakt da');
      blocked = true;
    }
    if (!blocked) {
      const gap = Math.min(
        ...mine.map((x) =>
          Date.parse(x.end_time) <= from
            ? (from - Date.parse(x.end_time)) / H
            : Date.parse(x.start_time) >= to
              ? (Date.parse(x.start_time) - to) / H
              : Number.POSITIVE_INFINITY,
        ),
      );
      if (gap < REST) why.push(`${Math.round(gap * 10) / 10} t hvile`);
      if (r.week.norm != null && r.week.hours + len > r.week.norm) why.push('over avtalt uke');
      if (r.user.birth_date_missing) why.push('mangler fødselsdato');
    }
    out.push({
      userId: r.user.id,
      name: fullName(r.user),
      hours: r.week.hours,
      norm: r.week.norm,
      kind: blocked ? 'blocked' : why.length ? 'review' : 'clear',
      why,
    });
  }
  const rank = { clear: 0, review: 1, blocked: 2 };
  return out.sort(
    (a, b) => rank[a.kind] - rank[b.kind] || a.hours - b.hours || a.name.localeCompare(b.name, 'nb'),
  );
}
